<script setup>
import { useGtag } from "vue-gtag-next";

const { fetchShortPage, fetchBrandsLotties, fetchShorts, fetchMedias } =
    useFetchAPI();

const { setMeta } = useMeta();
const { locale, t } = useI18n();
const { stopLoading, startLoading } = useAppState();
const { setHeaderMode } = useMode();
const { event } = useGtag();
const nuxtApp = useNuxtApp();

const PAGE_SIZE = 20;

const [{ data: pageData }, { data: videosData }, { data: lottiesUrl }] =
    await Promise.all([fetchShortPage(), fetchShorts(), fetchBrandsLotties()]);

const localeData = computed(() => {
    return videosData.value.filter((item) => item.language === locale.value);
});

const categoryList = computed(() => {
    return nuxtApp?.payload?.data?.["fetchPostCategory-short-" + locale.value];
});

const mediaIds = computed(() => {
    return [
        lottiesUrl.value.pc,
        lottiesUrl.value.tablet,
        lottiesUrl.value.mobile,
        pageData.value.featured_media,
        ...videosData.value.map((item) => item.featured_media),
    ];
});

const { data: mediaData } = await fetchMedias(mediaIds.value, "short-media");

// 將完整 URL 轉換為相對路徑，讓 Nuxt proxy 處理避免 CORS 問題
const toRelativeUrl = (url) => {
    if (!url) return null;
    try {
        const urlObj = new URL(url);
        return urlObj.pathname;
    } catch {
        return url;
    }
};

const { data: lottiesData } = await useAsyncData("lottie-data", async () => {
    const pcUrl = toRelativeUrl(
        mediaData.value.find((item) => item.id == lottiesUrl.value.pc)?.source_url
    );
    const tabletUrl = toRelativeUrl(
        mediaData.value.find((item) => item.id == lottiesUrl.value.tablet)?.source_url
    );
    const mobileUrl = toRelativeUrl(
        mediaData.value.find((item) => item.id == lottiesUrl.value.mobile)?.source_url
    );

    const [pcData, tabletData, mobileData] = await Promise.all([
        pcUrl ? $fetch(pcUrl) : null,
        tabletUrl ? $fetch(tabletUrl) : null,
        mobileUrl ? $fetch(mobileUrl) : null,
    ]);

    return {
        pc: {
            url: lottiesUrl.value?.pc?.url || "",
            data: pcData,
        },
        tablet: {
            url: lottiesUrl.value?.tablet?.url || "",
            data: tabletData,
        },
        mobile: {
            url: lottiesUrl.value?.mobile?.url || "",
            data: mobileData,
        },
    };
});

const categoryList1 = computed(() => {
    const categories =
        categoryList?.value
            ?.filter((item) => item.language === locale.value)
            .map((item) => item.name) || [];
    return [t("toolbar.filter.options.gplayPage.all"), ...categories];
});

const categoryList2 = computed(() => {
    const categories =
        pageData?.value?.meta_box?.selectedType?.map((item) => {
            return categoryList?.value?.find((category) => category.id == item)
                ?.name;
        }) || [];
    return [t("toolbar.filter.options.gplayPage.all"), ...categories];
});

const filterConfig = {
    wheels: [
        {
            data: categoryList1.value,
        },
    ],
    initValue: t("toolbar.filter.options.gplayPage.all"),
};

const sortConfig = {
    wheels: [
        {
            data: [
                t("toolbar.sort.options.gplayPage.newest"),
                t("toolbar.sort.options.gplayPage.oldest"),
            ],
        },
    ],
    initValue: t("toolbar.sort.options.gplayPage.newest"),
};

const titleText = ref(null);

// lazy loading
const displayedVideos = ref([]);
const currentPage = ref(1);
const isLoading = ref(false);
const hasMore = ref(true);

// search
const keyword = ref("");
const isSearching = ref(false);

// filter
const {
    currentFilter__main,
    currentFilter__hastag,
    filteredVideos,
    videoSection,
} = useFilterVideo();
const currentSort = ref(t("toolbar.sort.options.gplayPage.newest"));
const filterSelectRef = ref(null);

const loadVideos = async () => {
    if (isLoading.value) return;

    isLoading.value = true;

    // 模擬載入延遲
    await new Promise((resolve) => setTimeout(resolve, 800));

    const start = (currentPage.value - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    const newVideos = filteredVideos.value.slice(start, end);

    // 確保過濾後的數據不為空
    if (newVideos.length > 0) {
        displayedVideos.value = [...displayedVideos.value, ...newVideos];
        hasMore.value = end < filteredVideos.value.length;
    } else {
        // 沒有數據時清空顯示並設置hasMore為false
        hasMore.value = false;
    }

    isLoading.value = false;

    if (currentPage.value === 1) {
        stopLoading();
    }
};

const loadMoreVideos = () => {
    if (isLoading.value || !hasMore.value) return;
    currentPage.value++;
    setTimeout(() => {
        loadVideos();
    }, 800);
};

// 搜索影片
const search = (searchKeyword) => {
    keyword.value = searchKeyword;

    if (!searchKeyword.trim()) {
        cancelSearch();
        return;
    }

    event("search", {
        search_term: searchKeyword.trim(),
        event_category: "gplay",
        event_label: "search",
    });

    // 在標題或描述中搜索關鍵詞
    filteredVideos.value = localeData.value.filter(
        (video) =>
            video.title
                .join(" ")
                .toLowerCase()
                .includes(searchKeyword.toLowerCase()) ||
            video.description
                .join(" ")
                .toLowerCase()
                .includes(searchKeyword.toLowerCase()),
    );

    // 重置分頁
    currentPage.value = 1;
    displayedVideos.value = [];

    // 載入搜索結果
    loadVideos();

    // 標記為搜索狀態
    isSearching.value = true;
};

const cancelSearch = () => {
    keyword.value = "";

    // 重置篩選條件
    currentFilter__main.value = t("toolbar.filter.options.gplayPage.all");
    currentFilter__hastag.value = t("toolbar.filter.options.gplayPage.all");
    currentSort.value = t("toolbar.sort.options.gplayPage.newest");

    // 重置影片列表
    filteredVideos.value = [...localeData.value];

    // 重置分頁
    currentPage.value = 1;
    displayedVideos.value = [];

    // 標記為非搜索狀態（必須在loadVideos之前設置）
    isSearching.value = false;

    // 重新載入第一頁
    hasMore.value = true; // 確保可以載入更多
    loadVideos();
};

const changeFilter = (filter) => {
    currentFilter__main.value = filter;
    currentFilter__hastag.value = "";

    // 如果切換到"全部"，重置為非搜索狀態
    if (filter === t("toolbar.filter.options.gplayPage.all")) {
        isSearching.value = false;
        // 重置影片列表
        filteredVideos.value = [...localeData.value];
        // 重置分頁
        currentPage.value = 1;
        displayedVideos.value = [];
        // 排序並加載
        sortVideos(currentSort.value, true);
        // 載入第一頁
        loadVideos();
    } else {
        filterVideos(filter);
    }
};

const changeFilter2 = (filter) => {
    currentFilter__hastag.value = filter;
    currentFilter__main.value = "";

    // 如果有引用到 InputSelect 组件，重置其 model 值
    if (filterSelectRef.value) {
        filterSelectRef.value.model = "";
    }

    // 如果切換到"全部"，重置為非搜索狀態
    if (filter === t("toolbar.filter.options.gplayPage.all")) {
        isSearching.value = false;
        // 重置分頁
        currentPage.value = 1;
        displayedVideos.value = [];

        // 重置影片列表
        filteredVideos.value = [...localeData.value];

        // 排序
        sortVideos(currentSort.value, true);

        // 載入第一頁
        loadVideos();
    } else {
        filterVideos(filter);
    }
};

// 篩選影片
const filterVideos = (filter) => {
    if (filter === t("toolbar.filter.options.gplayPage.all")) {
        // 當選擇"全部"時，不進行篩選，顯示所有影片
        filteredVideos.value = [...localeData.value];

        // 如果兩個篩選器都是"全部"，不應隱藏精選和最新影片
        if (
            currentFilter__hastag.value ===
                t("toolbar.filter.options.gplayPage.all") ||
            currentFilter__main.value ===
                t("toolbar.filter.options.gplayPage.all")
        ) {
            isSearching.value = false;
        } else {
            isSearching.value = true;
        }
    } else {
        // 按照所選類別進行篩選
        filteredVideos.value = localeData.value.filter((video) =>
            video.categories.includes(filter),
        );

        // 標記為搜索狀態，隱藏精選和最新影片
        isSearching.value = true;
    }

    // 重置分頁
    currentPage.value = 1;
    displayedVideos.value = [];

    // 排序
    sortVideos(currentSort.value, false);

    // 載入第一頁
    loadVideos();
};

const changeSort = (sort) => {
    currentSort.value = sort;
    sortVideos(sort, false);
};

// 排序影片
const sortVideos = (sort, fromInit = false) => {
    if (sort === t("toolbar.sort.options.gplayPage.newest")) {
        filteredVideos.value.sort(
            (a, b) => new Date(b.date) - new Date(a.date),
        );
    } else {
        filteredVideos.value.sort(
            (a, b) => new Date(a.date) - new Date(b.date),
        );
    }

    // 重置分頁
    currentPage.value = 1;
    displayedVideos.value = [];

    // 載入第一頁
    loadVideos();

    // 只有在非初始化時才標記為搜索狀態
    if (!fromInit) {
        isSearching.value = true;
    }
};

watch(locale, () => {
    startLoading();
    setHeaderMode("light");
});

onMounted(async () => {
    // 初始化篩選值
    currentFilter__main.value = t("toolbar.filter.options.gplayPage.all");
    currentFilter__hastag.value = t("toolbar.filter.options.gplayPage.all");
    // 初始化篩選後的影片列表
    filteredVideos.value = [...localeData.value];
    // 應用預設排序 (傳入true表示這是初始化)
    sortVideos(currentSort.value, true);
    // 標記為非搜索狀態
    isSearching.value = false;
    // 載入第一頁
    await loadVideos();
    // 標題動態
    titleText.value.animate({ duration: 0.8, stagger: 0.05, delay: 1.5 });
});

await setMeta(
    pageData.value,
    mediaData.value.find((item) => item.id == pageData.value.featured_media),
);
</script>

<template>
    <div
        id="shorts"
        class="flex flex-col items-center justify-center bg-base-black"
    >
        <div class="slt-container overflow-hidden">
            <!-- hero -->
            <div
                id="hero"
                class="relative aspect-[393/462] w-full sm:aspect-[768/462] lg:mx-[auto] lg:mt-[calc(var(--header-height)+3.44vw)] lg:aspect-[1432/392] lg:w-[94.84%] 3xl:mt-[calc(var(--header-height)+66px)]"
            >
                <ClientOnly>
                    <LottieAnimation
                        :animationData="lottiesData?.pc?.data"
                        class="hidden aspect-[1432/392] overflow-hidden lg:block lg:rounded-[1.59vw] lg:border lg:border-base-dark 3xl:rounded-[30.5px]"
                    />
                    <LottieAnimation
                        :animationData="lottiesData?.tablet?.data"
                        class="hidden aspect-[768/462] overflow-hidden sm:block lg:hidden"
                    />
                    <LottieAnimation
                        :animationData="lottiesData?.mobile?.data"
                        class="aspect-[393/462] overflow-hidden sm:hidden"
                    />
                </ClientOnly>
                <div
                    class="slt-h2 absolute left-[5.85vw] top-[26vw] text-base-white xs:left-[23px] xs:top-[102px] sm:bottom-[calc(var(--window-height)*0.0586)] sm:left-[5.21vw] sm:top-[unset] lg:bottom-[50%] lg:left-[13.62%] lg:translate-y-[50%]"
                >
                    <BaseFormatText2
                        :text="pageData?.meta_box?.title"
                        ref="titleText"
                        type="h1"
                        :isAutoRevert="false"
                    />
                </div>
            </div>
            <!-- search bar -->
            <div
                id="search-bar"
                class="relative mt-[-8.65vw] flex w-full flex-col xs:mt-[-34px] sm:mt-[calc(var(--window-height)*-0.0293)] lg:-mt-5"
            >
                <div class="sm:px-[3.19vw] sm:py-0 lg:px-0">
                    <div
                        class="relative flex w-full items-center justify-center gap-[2.03vw] rounded-full bg-base-black px-[3.05vw] py-[2.54vw] xs:gap-[8px] xs:px-[12px] xs:py-[10px] sm:gap-[2.08vw] sm:p-[1.56vw] lg:mx-[auto] lg:w-[77.51%] lg:gap-4 lg:border-t lg:border-base-dark lg:p-[1.06vw] 3xl:p-[20.3px]"
                    >
                        <InputSearch
                            variant="dark"
                            :placeholder="t('toolbar.search.placeholder.gplay')"
                            @update:keyword="search"
                            @cancelSearch="cancelSearch"
                        />
                        <InputSelect
                            ref="filterSelectRef"
                            selectId="idFilter"
                            :config="filterConfig"
                            type="filter"
                            variant="dark"
                            @change="changeFilter"
                            v-sendGAsignal="{
                                event_category: 'gplay',
                                event_label: 'filter',
                            }"
                        ></InputSelect>
                        <InputSelect
                            selectId="idSort"
                            :config="sortConfig"
                            type="sort"
                            variant="dark"
                            @change="changeSort"
                            v-sendGAsignal="{
                                event_category: 'gplay',
                                event_label: 'sort',
                            }"
                        ></InputSelect>
                    </div>
                </div>
                <div
                    class="relative mt-[-4px] py-[4.07vw] xs:py-[16px] sm:mt-[4px] lg:mt-2"
                >
                    <div class="slt-dark-slider-mask left-0 lg:hidden"></div>
                    <div class="slt-scrollbar-hide w-auto overflow-x-auto">
                        <div
                            class="relative ml-[4.07vw] inline-flex w-max flex-row flex-nowrap gap-[3.05vw] pr-[20.36vw] xs:ml-[16px] xs:gap-[12px] xs:pr-[80px] sm:ml-[3.19vw] sm:pl-[2.08vw] sm:pr-[4.75vw] lg:mx-[auto] lg:flex lg:max-w-[77.51%] lg:gap-[0.79vw] lg:px-[1.06vw] 3xl:gap-[15.2px] 3xl:px-[20.3px]"
                        >
                            <div
                                v-for="(type, index) in categoryList2"
                                :key="type"
                                class="flex h-[10.18vw] shrink-0 cursor-pointer select-none items-center justify-center rounded-[18.32vw] px-[5.09vw] xs:h-[40px] xs:rounded-[72px] xs:px-[20px] lg:px-[1.32vw] 3xl:px-[25.4px]"
                                :class="{
                                    'bg-primary-orange text-base-black':
                                        currentFilter__hastag === type,
                                    'bg-base-deepGrey text-base-white':
                                        currentFilter__hastag !== type,
                                }"
                                @click="changeFilter2(type)"
                                v-sendGAsignal="{
                                    event_category: 'gplay',
                                    event_label: `篩選 ${type}`,
                                }"
                            >
                                <p class="slt-h6">{{ type }}</p>
                            </div>
                        </div>
                    </div>
                    <div
                        class="slt-dark-slider-mask right-0 -translate-x-[150%] scale-x-[-4] lg:hidden"
                    ></div>
                </div>
            </div>
            <div v-if="!isSearching">
                <div
                    v-for="(item, index) in pageData?.meta_box?.section"
                    :key="item"
                    class="overflow-hidden xs:mt-[36px] sm:mt-[48px] lg:mt-16"
                    :class="{
                        'mt-[9.16vw]': index === 0,
                        'mt-[18.83vw]': index === 1,
                    }"
                >
                    <VideoList
                        :title="item.name"
                        :videos="
                            item.shorts.map((shortId) =>
                                localeData.find((video) => video.id == shortId),
                            )
                        "
                        @click="videoSection = 'special'"
                    />
                </div>
            </div>
            <div
                id="all"
                class="mt-[12.21vw] xs:mt-[48px] sm:mt-[120px] lg:mt-20"
                :class="{ hidden: !displayedVideos.length && !isLoading }"
            >
                <VideoList
                    :title="
                        displayedVideos.length
                            ? isSearching
                                ? [t('gplay.searchResult')]
                                : [t('gplay.allContent')]
                            : ['']
                    "
                    :videos="displayedVideos"
                    :isScroll="false"
                    :isLoading="isLoading"
                    :showLoadMore="true"
                    :hasMore="hasMore"
                    :highlightKeyword="keyword"
                    @loadMore="loadMoreVideos"
                    @click="videoSection = 'all'"
                />
            </div>
            <div
                v-if="!displayedVideos.length && !isLoading"
                class="slt-h4 mb-[64px] mt-[54px] text-center text-base-white sm:mb-[134px] sm:mt-[100px] lg:mb-[160px] lg:mt-[104px]"
            >
                {{ $t("gplay.noResult") }}
            </div>
        </div>
        <Transition name="fade" mode="out-in">
            <div class="w-full">
                <NuxtPage :data="videosData" />
            </div>
        </Transition>
    </div>
</template>

<style scoped>
.slt-dark-slider-mask {
    @apply pointer-events-none absolute top-0 h-full w-[5.09vw] bg-[linear-gradient(270deg,rgba(0,0,0,0)_0%,#000_100%)] xs:w-[20px];
}

/* transition */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease-in-out;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
    opacity: 1;
}
</style>
